import { ReactNode } from 'react';

export interface KeyValuePair {
    key?: string;
    label?: string;
    value: ReactNode;
}

/**
 * The complete query context emitted to the page whenever anything changes.
 * The page receives this, builds its API URL/params from it, and re-fetches.
 * Extend this interface when new query dimensions are added (filters, groupBy, etc.).
 */
export interface TableQuery {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    page: number;
    pageSize: number;
}

/**
 * Identifies which interaction triggered the query change.
 * Useful when the page needs to reset page back to 0 on sort,
 * or handle different dimensions differently.
 */
export enum TableQueryEvent {
    SORT = 'sort',
    PAGE = 'page',
    PAGE_SIZE = 'pageSize'
    // future: FILTER = 'filter', GROUP_BY = 'groupBy', ...
}

// ─── Column definition ────────────────────────────────────────────────────────

export interface DataTableColumn<T = any> {
    text: string | ReactNode;
    value: string;
    width?: number | string;
    sortable?: boolean;
    render?: (row: T, index: number) => ReactNode;
    align?: 'left' | 'center' | 'right' | 'inherit' | 'justify';
    title?: string | ((row: T) => string);
    isAction?: boolean;
    multiLine?: boolean;
    multiLineValue?: (row: T) => KeyValuePair[];
    maxHeight?: number | string;
    overflowY?: 'auto' | 'hidden' | 'scroll';
    isTitle?: boolean;
    isHidden?: (row: T) => boolean;

    // ── Per-column sort customisation (client-side only)

    /**
     * Extract the raw value used for comparison during client-side sorting.
     * Use when `render` displays something different from what should be sorted on.
     *
     * Example: column renders a formatted date but should sort by epoch:
     *   valueGetter: (row) => row.createdAt.toMillis()
     *
     * When omitted, falls back to `row[column.value]` (dot-path supported).
     */
    valueGetter?: (row: T) => any;

    /**
     * Full custom comparator for this column. Overrides `valueGetter` and the
     * default comparison. Return negative / 0 / positive like Array.sort.
     * Write it as ascending — direction multiplier is applied automatically.
     *
     * Example: case-insensitive sort:
     *   comparator: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
     */
    comparator?: (a: T, b: T) => number;
}

// ─── Pagination ───────────────────────────────────────────────────────────────

export interface DataTablePagination {
    count: number;
    page?: number;
    rowsPerPage?: number;
    onPageChange?: (event: unknown, newPage: number) => void;
    onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    rowsPerPageOptions?: number[];
}

// ─── Sorting ──────────────────────────────────────────────────────────────────

/**
 * Sorting configuration. Two modes — detected automatically.
 *
 * ── CLIENT-SIDE (default) ─────────────────────────────────────────────────────
 * Table owns sort state and sorts `data` internally.
 * Set `defaultSortBy` / `defaultSortDirection` for initial state.
 * Per-column customisation via `column.valueGetter` or `column.comparator`.
 * Global fallback via `WcnTableProps.customSort`.
 *
 * ```ts
 * sorting={{ defaultSortBy: 'name' }}
 * ```
 *
 * ── SERVER-SIDE ───────────────────────────────────────────────────────────────
 * Table is a controlled display component — never touches `data`.
 * Provide `sortBy` + `sortDirection` (mirrors API state).
 * The table calls `onSortChange` when the user clicks a header.
 * Use `onQueryChange` instead when you also want page/pageSize in the same callback.
 *
 * ```ts
 * sorting={{
 *   sortBy: query.sortBy,
 *   sortDirection: query.sortDir,
 *   onSortChange: (field, dir) => fetchData({ sortBy: field, sortDir: dir })
 * }}
 * ```
 */
export interface DataTableSorting {
    /** CLIENT-SIDE — initial sort field. */
    defaultSortBy?: string;
    /** CLIENT-SIDE — initial sort direction (default: 'asc'). */
    defaultSortDirection?: 'asc' | 'desc';

    /** SERVER-SIDE — controlled sort field (mirrors API state). */
    sortBy?: string;
    /** SERVER-SIDE — controlled sort direction (mirrors API state). */
    sortDirection?: 'asc' | 'desc';

    /**
     * SERVER-SIDE — presence activates server-side mode.
     * Use this when sort is the only server dimension.
     * For sort + pagination together, prefer `WcnTableProps.onQueryChange`.
     */
    onSortChange?: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
}

// ─── Selection ────────────────────────────────────────────────────────────────

export interface DataTableSelection<T = any> {
    selectedItems: T[];
    onSelectionChange: (items: T[]) => void;
    getItemKey: (item: T) => string | number;
    selectionMode?: 'single' | 'multiple';
}

// ─── Expansion ────────────────────────────────────────────────────────────────

export interface DataTableExpansion<T = any, TChild = any> {
    expandedRows?: (string | number)[];
    onExpandToggle?: (rowKey: string | number) => void;
    getRowKey: (item: T) => string | number;
    renderExpandedContent?: (row: T) => ReactNode;
    showExpandIconCondition?: (row: T) => boolean;
    nestedTable?: {
        columns: DataTableColumn<TChild>[];
        data: TChild[];
        getChildRows: (parentRow: T, allData: TChild[]) => TChild[];
        size?: 'small' | 'medium';
        noDataMessage?: string;
        paddingLeft?: number;
        expansion?: Omit<DataTableExpansion<TChild>, 'expandedRows' | 'onExpandToggle'>;
    };
}

// ─── Table props ──────────────────────────────────────────────────────────────

export interface WcnTableProps<T = any> {
    readonly title?: string;
    readonly columns: DataTableColumn<T>[];
    readonly data?: T[];
    readonly loading?: boolean;
    readonly pagination?: DataTablePagination;
    readonly sorting?: DataTableSorting;

    /**
     * Unified server-side query callback.
     *
     * Use this instead of `sorting.onSortChange` + `pagination.onPageChange`
     * when the table drives server-side data fetching for sort AND pagination.
     *
     * Called with the full `TableQuery` context whenever sort, page, or pageSize changes.
     * Also receives `event` so the handler can react differently per interaction type
     * (e.g. reset page to 0 when sort changes).
     *
     * The table becomes fully controlled when this is provided:
     * - `sorting.sortBy` + `sorting.sortDirection` must reflect API state
     * - `pagination.page` + `pagination.rowsPerPage` must reflect API state
     *
     * ```ts
     * onQueryChange={(query, event) => {
     *   if (event === TableQueryEvent.SORT) {
     *     setQuery({ ...query, page: 0 }); // reset to first page on sort
     *   } else {
     *     setQuery(query);
     *   }
     *   fetchData(query);
     * }}
     * ```
     */
    readonly onQueryChange?: (query: TableQuery, event: TableQueryEvent) => void;

    /**
     * Global custom sort function for client-side mode.
     * Runs for any column that does NOT define its own `column.comparator`.
     * Do not mutate the input array — return a new sorted array.
     */
    readonly customSort?: (data: T[], sortBy: string, sortDir: 'asc' | 'desc') => T[];

    readonly selection?: DataTableSelection<T>;
    readonly expansion?: DataTableExpansion<T>;
    readonly noDataMessage?: string;
    readonly onRowClick?: (row: T, index: number) => void;
    readonly getRowProps?: (row: T, index: number) => Record<string, any>;
    readonly size?: 'small' | 'medium';
    readonly renderEmpty?: () => ReactNode;
    readonly renderLoading?: () => ReactNode;
    readonly mobileView?: 'auto' | 'scroll' | 'card';
}

// ─── Nested table render props ────────────────────────────────────────────────

export interface NestedTableRenderProps {
    columns: DataTableColumn<any>[];
    data: any[];
    size?: 'small' | 'medium';
    noDataMessage?: string;
    expansion?: DataTableExpansion<any>;
    mobileView?: 'auto' | 'scroll' | 'card';
}
